import {RamonagePdfReport, pdfService} from "@mytour/pdf";
import {useAccount, useCurrentCompany, useCurrentUserStyle} from "@mytour/platform";
import type {
  CertificatRamonageDetails,
  CertificatRamonagePayload,
  ICustomer,
  IEvent,
} from "@mytour/shared";
import axios from "axios";
import {useRef, useState} from "react";
import {toast} from "react-toastify";
import {
  useCertificate,
  useCompleteCertificate,
  useLatestCertificateByCustomer,
} from "../query/certificateQueries";
import {RamonagePhotoPreparationError, prepareCertificatePhotos} from "./prepareCertificatePhotos";

export function useCertificateAction(
  event: IEvent,
  customer: ICustomer,
  onPerformed: () => void | Promise<void>
) {
  const [showModal, setShowModal] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const completing = useRef(false);
  const {company} = useCurrentCompany();
  const {platformUserStyle} = useCurrentUserStyle();
  const {account} = useAccount();
  const {
    certificate,
    isCertificateLoading,
    isCertificateFetching,
    certificateError,
    refetchCertificate,
  } = useCertificate(event.id as string, !!event.id);
  const {latestCertificate, isLatestCertificateLoading} = useLatestCertificateByCustomer(
    customer.id as string,
    !!customer.id
  );
  const {completeCertificate, isCompletingCertificate} = useCompleteCertificate();
  const intervenantName = account ? `${account.firstName} ${account.lastName}` : undefined;
  const isLoading =
    isCertificateLoading ||
    (!certificate &&
      !certificateError &&
      (isLatestCertificateLoading || !company || !platformUserStyle));

  return {
    isLoading,
    hasCertificate: !!certificate,
    needsCompletion: certificate?.hasFile === false,
    certificate,
    certificateError,
    refetchCertificate,
    isCertificateFetching,
    latestCertificate,
    company,
    platformUserStyle,
    intervenantName,
    isPending: isCompletingCertificate,
    showModal,
    saveError,
    openModal: () => {
      setSaveError(null);
      setShowModal(true);
    },
    closeModal: () => setShowModal(false),
    handleSave,
    handleResume,
  };

  async function generatePdf(savedCertificate: CertificatRamonageDetails) {
    if (!company || !platformUserStyle) {
      throw new Error("Certificate presentation unavailable");
    }
    return pdfService.toBlob(
      <RamonagePdfReport
        data={savedCertificate}
        company={company}
        customer={customer}
        pdfStyle={pdfService.getStyle(platformUserStyle)}
        intervenantName={savedCertificate.intervenantName ?? undefined}
      />
    );
  }

  async function handleSave(data: CertificatRamonagePayload, sendEmail: boolean) {
    if (!event.id || !company || !platformUserStyle || completing.current) {
      return;
    }
    completing.current = true;
    let saved = false;
    setSaveError(null);
    try {
      const result = await completeCertificate({
        eventId: event.id,
        payload: data,
        sendEmail,
        customer,
        generatePdf,
        preparePayload: (payload) =>
          prepareCertificatePhotos(payload, (prepared) =>
            generatePdf({
              ...prepared,
              certificateNumber: "9999-99999999999999999999",
              hasFile: false,
              intervenantName: intervenantName ?? null,
            })
          ),
        onSaved() {
          saved = true;
          setShowModal(false);
        },
      });
      await onPerformed();
      if (result.emailStatus === "failed") {
        toast.warning(result.emailMessage ?? "Attestation enregistrée, mais l'email a échoué.");
      } else if (result.emailStatus === "uncertain") {
        toast.warning("Attestation enregistrée. L’envoi de l’email est à vérifier.");
      }
    } catch (error) {
      if (isSignatureModelConflict(error)) {
        throw error;
      }
      if (saved) {
        toast.error(
          "L'attestation est sauvegardée, mais sa finalisation n'a pas pu être confirmée. Vérifiez son état et réessayez si nécessaire."
        );
      } else {
        setSaveError(
          error instanceof RamonagePhotoPreparationError
            ? error.message
            : "L'enregistrement n'a pas pu être confirmé. Réessayez pour vérifier et reprendre l'attestation."
        );
      }
    } finally {
      completing.current = false;
    }
  }

  async function handleResume() {
    if (!event.id || !company || !platformUserStyle || completing.current) {
      return;
    }
    completing.current = true;
    try {
      await completeCertificate({eventId: event.id, sendEmail: false, customer, generatePdf});
      await onPerformed();
      toast.success("Attestation finalisée. Vous pouvez la télécharger ou l'envoyer.");
    } catch {
      toast.error(
        "La finalisation n'a pas pu être confirmée. Vérifiez l'état de l'attestation et réessayez si nécessaire."
      );
    } finally {
      completing.current = false;
    }
  }
}

function isSignatureModelConflict(error: unknown) {
  return (
    axios.isAxiosError(error) &&
    error.response?.status === 409 &&
    error.response.data?.code === "SIGNATURE_MODEL_CHANGED"
  );
}
